import type { Lesson } from '../types/lesson';
import type { Order } from '../types/order';
import type { UserProfile } from '../types/user';
import { useStorageDownloadUrl } from '../hooks/useStorageDownloadUrl';
import { useState } from 'react';
import LessonStatusIcon from './LessonStatusIcon';
import { formatLessonDate } from '../utils/lessonDate';

interface CoachLessonCardProps {
  lesson: Lesson;
  order: Order;
  playerProfile?: UserProfile;
  onClick: () => void;
  isProcessing?: boolean;
  /** Set by the list when this row is the open one. */
  selected?: boolean;
}

// Resolve a real player name. Rejects the literal "player"/"Player" sentinel
// (test-seed data sometimes leaks this) and falls back through the joined
// playerProfile → email local-part → generic. Console-warns on fallback so
// missing-displayName cases are visible during dev.
function resolvePlayerName(order: Order, playerProfile?: UserProfile): string {
  const candidates = [
    playerProfile?.displayName,
    playerProfile?.playerProfile?.displayName,
    order.playerName,
  ];
  for (const c of candidates) {
    const s = c?.trim();
    if (s && s.toLowerCase() !== 'player') return s;
  }
  const email = playerProfile?.email;
  if (email) {
    const localPart = email.split('@')[0];
    if (localPart) {
      // eslint-disable-next-line no-console
      console.warn('[CoachLessonCard] Player has no displayName; falling back to email local-part:', email);
      return localPart;
    }
  }
  // eslint-disable-next-line no-console
  console.warn('[CoachLessonCard] Player has no displayName or email — using generic fallback', { playerId: order.playerId });
  return 'Tennis Player';
}

/** First frame of the player's submission, or null when the lesson has no
 *  thumbnail strip. Mirrors firstFramePath in PlayerLessonCard; duplicated
 *  rather than imported so this pass touches no player-side file. */
function firstFramePath(lesson?: Lesson | null): string | null {
  const base = lesson?.playerVideoThumbnailsBasePath;
  if (!base || (lesson?.playerVideoThumbnailCount ?? 0) === 0) return null;
  return `${base}/0.jpg`;
}

/** Month + day. The status line is too tight to carry a year. */
function playerInitials(name: string): string {
  return name.split(' ').filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';
}

// Inline relative-time formatter — avoids pulling in date-fns just for this.
// Returns "just now" / "5m ago" / "2h ago" / "3d ago" / "1w ago" / "2mo ago".

// eslint-disable-next-line react-refresh/only-export-components
export function coachLessonTypeLabel(lesson: Lesson, order: Order): string {
  if (order.type === 'training_program') {
    return `Training Program · Lesson ${lesson.lessonNumber ?? '?'} of ${order.totalLessons ?? '?'}`;
  }
  if (order.type === 'shot_consult') return 'Shot Consult';
  return 'Single Lesson';
}


export default function CoachLessonCard({
  selected = false, lesson, order, playerProfile, onClick, isProcessing }: CoachLessonCardProps) {
  const isTransferred = !!(lesson.isTransferred || order.isTransferred);
  const name = resolvePlayerName(order, playerProfile);
  // Live profile takes priority; order snapshot is the fallback so the
  // avatar updates immediately after the player re-uploads their photo.
  const avatarUrl = playerProfile?.playerProfile?.avatarUrl || playerProfile?.avatarUrl || order.playerAvatarUrl;

  const typeLabel = coachLessonTypeLabel(lesson, order);
  // Player level, worth a glance before a coach opens the review.
  const utr = (playerProfile?.playerProfile as Record<string, unknown> | undefined)?.utr as number | undefined
    ?? (playerProfile as Record<string, unknown> | undefined)?.utr as number | undefined;

  // Treat reserved+playerVideoUrl as effectively in_progress: the video was uploaded
  // but the CF hasn't flipped the Firestore status yet. Don't show "Awaiting upload".
  const isInProgress = lesson.status === 'in_progress' || (lesson.status === 'reserved' && !!lesson.playerVideoUrl);
  const isReserved = lesson.status === 'reserved' && !lesson.playerVideoUrl;
  const isDelivered = lesson.status === 'delivered';

  const thumbUrl = useStorageDownloadUrl(firstFramePath(lesson));
  const [thumbBroken, setThumbBroken] = useState(false);
  const showThumb = !!thumbUrl && !thumbBroken;

  /**
   * The status, and the date, kept apart.
   *
   * These used to be one string: the in-progress case read `Submitted ${relative}`,
   * which is not a key in LessonStatusIcon's STATUS_ICON map, so the lookup
   * missed and every in-progress row drew the bare fallback ring. It also meant
   * the row carried two dates, because shortDate appended a second one after
   * the separator: "Submitted 2d ago . Friday".
   *
   * Every branch is now a mapped key, and the date is rendered once beside it.
   */
  const statusLabel = isProcessing
    ? 'Sending...'
    : isInProgress
      ? 'Awaiting your review'
      : isReserved
        ? 'Awaiting upload'
        : isDelivered
          ? 'Delivered'
          : '';
  // Delivered rows date from delivery, everything else from the last update.
  const statusDate = formatLessonDate(isDelivered ? lesson.deliveredAt : (lesson.updatedAt ?? lesson.createdAt));

  // Secondary text on a filled biscayne row, matching the Messages sidebar.
  const secondary = selected ? 'text-white/80' : 'text-[rgba(60,60,67,0.6)]';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      data-order-id={order.id}
      data-lesson-row
      className={`ios-press cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40 ${
        selected ? '' : 'border-b border-b-[#e6e6e6]'
      }`}
      style={{ padding: '12px 16px' }}
    >
      <div className="flex items-start gap-3">
        {/* Leading slot — the player's own footage when we have a frame for it,
            otherwise their avatar. Never a grey placeholder: reserved lessons
            have no video and older lessons predate thumbnail generation. */}
        {showThumb ? (
          <img
            src={thumbUrl!}
            alt=""
            onError={() => setThumbBroken(true)}
            className="w-11 h-11 object-cover flex-shrink-0 bg-black"
            style={{ borderRadius: 8 }}
          />
        ) : (
          /* Oasis, matching the player's list rows — this was the one row in
             either list filling its circle with biscayne. */
          <div className="w-11 h-11 rounded-full flex items-center justify-center overflow-hidden text-[15px] font-bold text-white flex-shrink-0" style={{ background: '#54C3EA' }}>
            {avatarUrl
              ? <img src={avatarUrl} alt={name} className="w-full h-full object-cover" />
              : playerInitials(name)}
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className={`text-[15px] leading-[20px] font-semibold truncate ${selected ? 'text-white' : 'text-[#1C1C1C]'}`}>{typeLabel}</div>
          <div className={`flex items-center gap-1.5 text-[13px] leading-[18px] min-w-0 ${secondary}`}>
            <span className="truncate">{name}</span>
            {isTransferred && (
              <span
                className="text-[9px] font-bold rounded-full uppercase whitespace-nowrap flex-shrink-0"
                style={{ background: 'rgba(255,237,220,.92)', color: '#FF8A50', padding: '1px 6px', letterSpacing: '.04em' }}
              >
                Transferred
              </span>
            )}
          </div>
          <div className={`flex flex-wrap items-center gap-x-[6px] mt-[2px] text-[13px] leading-[18px] min-w-0 ${secondary}`}>
            {statusLabel && <LessonStatusIcon label={statusLabel} selected={selected} />}
            {/* Nothing truncates because the screen is narrow, and
                nothing is dropped either. It reflows: below md the date takes
                its own line under the status. A coach needs to know how long a
                player has been waiting, so the date is not the thing to lose. */}
            <span className="shrink-0" data-testid={isProcessing ? 'lesson-status-processing' : `lesson-status-${lesson.status}`}>
              {statusLabel}
            </span>
            {statusDate && (
              <span className="hidden md:inline truncate shrink-0">· {statusDate}</span>
            )}
            {/* UTR rides the end of the status line at every width. It is 67
                wide, and with the date on its own line below md there is room
                for it beside the status rather than anywhere else. */}
            {utr != null && (
              <span className="whitespace-nowrap flex-shrink-0">· UTR {utr}</span>
            )}
            {statusDate && (
              <span className="md:hidden truncate shrink-0 basis-full pl-5">{statusDate}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
